<template>
  <div class="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-text-sm">
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1">
      <span class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">
        SolarEdge <span class="tw-font-normal tw-normal-case tw-text-gray-400">opgeslagen</span>
      </span>
      <slot name="actions" />
    </div>

    <div v-if="isFetching && !status" class="tw-flex tw-items-center tw-py-1 tw-text-gray-400">
      <span class="tw-w-4 tw-h-4 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin tw-mr-2" />
      Laden...
    </div>
    <div v-else-if="error" class="tw-text-red-600 tw-text-xs">
      {{ error.message || error.detail || 'SolarEdge-status ophalen mislukt' }}
    </div>
    <div v-else-if="status" class="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-1">
      <span class="tw-text-gray-500">Site</span>
      <span class="tw-font-medium">
        <a
          v-if="status.site_id"
          :href="`https://monitoring.solaredge.com/solaredge-web/p/site/${status.site_id}`"
          target="_blank"
          rel="noopener"
          class="tw-text-blue-600 hover:tw-underline"
        >{{ status.site_id }}</a>
        <template v-else>—</template>
        <span v-if="site?.activation_status" :class="site.activation_status === 'ACTIVE' ? 'tw-text-green-600' : 'tw-text-red-600'">
          · {{ site.activation_status }}
        </span>
      </span>
      <span class="tw-text-gray-500">Piekvermogen</span>
      <span class="tw-font-medium">{{ peakPower }}</span>
      <span class="tw-text-gray-500">Omvormers</span>
      <span class="tw-font-medium">
        {{ activeInverters.length }} actief<span v-if="activeInverters[0]?.part_number" class="tw-text-gray-400 tw-font-normal"> · {{ activeInverters[0].part_number }}</span>
      </span>
      <span class="tw-text-gray-500">Laatste meting</span>
      <span class="tw-font-medium" :class="lastSampleStale ? 'tw-text-red-600' : ''" :title="lastSample ? formatDateTimeNL(lastSample) : ''">
        {{ lastSample ? formatRelativeNL(lastSample) : '—' }}
      </span>
      <span class="tw-text-gray-500">Open meldingen</span>
      <span class="tw-font-medium" :class="alerts.length ? 'tw-text-red-600' : ''">{{ alerts.length || 'Geen' }}</span>
      <span class="tw-text-gray-500">Uitgelezen via</span>
      <span class="tw-font-medium">{{ status.switched ? 'SolarEdge' : 'Cast4all (nog niet omgezet)' }}</span>
    </div>
    <div v-for="(msg, i) in status?.warnings ?? []" :key="i" class="tw-text-xs tw-text-amber-700 tw-mt-1">{{ msg }}</div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useSolarEdgeStatus, formatPeakPower } from './composables/useSolarEdgeStatus'
import { formatDateTimeNL, formatRelativeNL } from './formatters'

// The stored SolarEdge state in a few rows, for the vendor column; the full panel (SolarEdgeStatus, with the
// live reads) opens in a modal. Same query as that panel, so opening it does not fetch again.
const props = defineProps({
  serialNumber: { type: String, required: true },
})

const { data: status, isFetching, error } = useSolarEdgeStatus(computed(() => props.serialNumber))

const site = computed(() => status.value?.stored?.site ?? null)
const peakPower = computed(() => formatPeakPower(site.value?.peak_power))
const activeInverters = computed(() => (status.value?.stored?.inverters ?? []).filter((i) => i.active !== false))
const alerts = computed(() => status.value?.stored?.open_alerts ?? [])

// the newest sample of any active inverter; red when older than a day
const lastSample = computed(() =>
  activeInverters.value.map((i) => i.last_sample_at).filter(Boolean).sort().pop() ?? null
)
const lastSampleStale = computed(() =>
  !!lastSample.value && Date.now() - new Date(lastSample.value).getTime() > 24 * 3600 * 1000
)
</script>
